import React, { FC, useState } from 'react';
import { Paper } from '@mui/material';
import moment from 'moment-timezone';

import { IAbsenceRequestParams, IAbsenceStatusDTO, IAbsenceTypeDTO } from 'src/api/dtos';
import {
  CalendarDateIcon,
  Dropdown,
  DropdownItem,
  FilterFilledIcon,
  FilterIcon,
  SortIcon,
  Status,
} from 'src/shared/components';
import { API_DATE_FORMAT } from 'src/shared/constants';
import { NotificationFlagsEnums, SortDirectionEnums } from 'src/shared/enums';
import { useTranslate } from 'src/shared/providers';
import { mapAbsenceStatusStyle } from 'src/shared/utils';

interface Props {
  types: IAbsenceTypeDTO[];
  statuses: IAbsenceStatusDTO[];
  onFiltersChange: (queryParams: IAbsenceRequestParams) => void;
}

enum DateFilterEnum {
  ALL = 0,
  PAST = 1,
  FUTURE = 2,
}

export const AbsenceDetailsFilters: FC<Props> = ({ types, statuses, onFiltersChange }) => {
  const { t } = useTranslate();
  const [queryParams, setQueryParams] = useState<IAbsenceRequestParams>({});

  const handleSort = (sortDirection: SortDirectionEnums) => {
    const filters: IAbsenceRequestParams = { ...queryParams, sortDirection };

    setQueryParams(filters);
    onFiltersChange(filters);
  };

  const handleDateRangeChange = (dateRange: DateFilterEnum) => {
    let filters: IAbsenceRequestParams = { ...queryParams };

    switch (dateRange) {
      case DateFilterEnum.FUTURE:
        filters = { ...filters, fromDate: moment().add(1, 'day').format(API_DATE_FORMAT), toDate: undefined };
        break;
      case DateFilterEnum.PAST:
        filters = { ...filters, fromDate: undefined, toDate: moment().subtract(1, 'day').format(API_DATE_FORMAT) };
        break;
      case DateFilterEnum.ALL:
      default:
        filters = { ...filters, fromDate: undefined, toDate: undefined };
    }

    setQueryParams(filters);
    onFiltersChange(filters);
  };

  const handleClear = (key: keyof IAbsenceRequestParams) => {
    const filters: IAbsenceRequestParams = { ...queryParams, [key]: undefined };

    setQueryParams(filters);
    onFiltersChange(filters);
  };

  const handleTypesChange = (typeId: number, newActive?: boolean) => {
    const absenceTypeIds = newActive
      ? [...(queryParams.absenceTypeIds || []), typeId]
      : queryParams.absenceTypeIds?.filter((id) => id !== typeId);

    const filters: IAbsenceRequestParams = { ...queryParams, absenceTypeIds };

    setQueryParams(filters);
    onFiltersChange(filters);
  };

  const handleStatusChange = (statusId: NotificationFlagsEnums, newActive?: boolean) => {
    const statusIds = newActive
      ? [...(queryParams.statusIds || []), statusId]
      : queryParams.statusIds?.filter((id) => id !== statusId);

    const filters: IAbsenceRequestParams = { ...queryParams, statusIds };

    setQueryParams(filters);
    onFiltersChange(filters);
  };

  return (
    <Paper elevation={4} className="w-100 mb-4 px-2 py-2 d-flex align-items-center justify-content-between">
      <div>
        <Dropdown
          defaultValues={[SortDirectionEnums.DESCENDING]}
          title={t('global.sortBy')}
          type="radio"
          icon={<SortIcon />}
          onSelect={handleSort}
        >
          <DropdownItem label={t('global.sortByLatestToOldest')} value={SortDirectionEnums.DESCENDING} />
          <DropdownItem label={t('global.sortByOldestToLatest')} value={SortDirectionEnums.ASCENDING} />
        </Dropdown>
      </div>
      <div className="d-flex align-items-center">
        <Dropdown
          showActive={true}
          defaultValues={[DateFilterEnum.ALL]}
          className="me-2"
          title={t('global.date')}
          showAriaText
          icon={<FilterIcon />}
          activeIcon={<FilterFilledIcon />}
          onSelect={handleDateRangeChange}
        >
          <DropdownItem
            className="mt-2 mb-3"
            label={<DropdownItemDateLabel label={t('global.showAll')} />}
            value={DateFilterEnum.ALL}
          />
          <DropdownItem
            className="mt-2 mb-3"
            label={<DropdownItemDateLabel label={t('global.showPastOnly')} />}
            value={DateFilterEnum.PAST}
            ariaText="(Past Only)"
          />
          <DropdownItem
            className="mt-2"
            label={<DropdownItemDateLabel label={t('global.showFutureOnly')} />}
            value={DateFilterEnum.FUTURE}
            ariaText="(Future Only)"
          />
        </Dropdown>
        <Dropdown
          showActive={true}
          className="me-2"
          title={t('absences.type')}
          type="checkbox"
          icon={<FilterIcon />}
          activeIcon={<FilterFilledIcon />}
          onSelect={handleTypesChange}
          onClear={() => handleClear('absenceTypeIds')}
        >
          {types.map((type, index) => (
            <DropdownItem key={`${type.id}-${index}`} label={type.description} value={type.id} />
          ))}
        </Dropdown>
        <Dropdown
          showActive={true}
          title={t('absences.status')}
          type="checkbox"
          icon={<FilterIcon />}
          activeIcon={<FilterFilledIcon />}
          onSelect={handleStatusChange}
          onClear={() => handleClear('statusIds')}
        >
          {statuses
            .filter((status) => status.statusId !== NotificationFlagsEnums.FORWARDED)
            .map((status, index) => {
              return (
                <DropdownItem
                  key={`${status.statusName}-${index}`}
                  label={<Status title={status.statusName} variant={mapAbsenceStatusStyle[status.statusId]} />}
                  value={status.statusId}
                />
              );
            })}
        </Dropdown>
      </div>
    </Paper>
  );
};

const DropdownItemDateLabel: FC<{ label: string }> = ({ label }) => {
  return (
    <div className="d-flex align-items-center">
      <CalendarDateIcon className="me-2" />
      <label className="d-block small fw-light">{label}</label>
    </div>
  );
};
